import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useQueries, useQuery } from '@tanstack/react-query'
import { Button, Card, Checkbox, Group, List, LoadingOverlay, Stack, Table, Text } from '@mantine/core'
import { modals } from '@mantine/modals'
import { TbRefresh } from 'react-icons/tb'
import { getAllVehiclesOptions, getControlDataDataVersionOptions } from '@/api/main/@tanstack/react-query.gen'
import type { ControlDataVersion } from '@/api/main/types.gen'
import PageShell from '@/components/Common/PageShell'
import { formatDateTime } from '@/utils/datetime'
import { useVehicleMapSync } from './useVehicleMapSync'
import {
  CONTROL_DATA_SCHEMAS,
  DATA_VERSION_MATCH_COLOR,
  HIGHLIGHTED_TEXT_COLOR,
  INVALID_SOFTWARE_VERSION_COLOR,
  isSoftwareVersionValid,
  matchDataVersion,
  softwareVersionField,
} from './vehicleVersions'
import type { ControlDataSchemaName } from './vehicleVersions'

const VehicleVersionsPage = () => {
  const { t } = useTranslation(['vehicles', 'common'])
  const { data: vehicles, isLoading } = useQuery(getAllVehiclesOptions())
  const { isSyncing, syncVehicles } = useVehicleMapSync()

  const backendVersions = useQueries({
    queries: CONTROL_DATA_SCHEMAS.map(schema =>
      getControlDataDataVersionOptions({ headers: { 'city-sense-control-data-schema': schema } })
    ),
    combine: results =>
      Object.fromEntries(CONTROL_DATA_SCHEMAS.map((schema, index) => [schema, results[index].data])) as Partial<
        Record<ControlDataSchemaName, ControlDataVersion>
      >,
  })

  const [selected, setSelected] = useState<Record<string, boolean>>({})
  const [skipVersionCheck, setSkipVersionCheck] = useState(false)
  const [skipLastSyncTimeCheck, setSkipLastSyncTimeCheck] = useState(false)

  const syncableVehicles = useMemo(
    () => (vehicles ?? []).filter(vehicle => !!vehicle.vpn_address).map(vehicle => vehicle.id),
    [vehicles]
  )

  const selectedVehicles = useMemo(
    () => syncableVehicles.filter(vehicleId => selected[vehicleId]),
    [syncableVehicles, selected]
  )

  const allSelected = syncableVehicles.length > 0 && selectedVehicles.length === syncableVehicles.length
  const someSelected = selectedVehicles.length > 0 && !allSelected

  const toggleAll = () =>
    setSelected(allSelected ? {} : Object.fromEntries(syncableVehicles.map(vehicleId => [vehicleId, true])))

  const toggleVehicle = (vehicleId: string, checked: boolean) =>
    setSelected(previous => ({ ...previous, [vehicleId]: checked }))

  const confirmSync = () =>
    modals.openConfirmModal({
      title: t('syncMaps'),
      children: (
        <Stack gap="xs">
          <Text size="sm">{t('confirmSyncMapsBody', { count: selectedVehicles.length })}</Text>
          <List size="sm" withPadding>
            {selectedVehicles.map(vehicleId => (
              <List.Item key={vehicleId}>{vehicleId}</List.Item>
            ))}
          </List>
          {(skipVersionCheck || skipLastSyncTimeCheck) && (
            <List size="sm" withPadding>
              {skipVersionCheck && <List.Item>{t('skipVersionCheck')}</List.Item>}
              {skipLastSyncTimeCheck && <List.Item>{t('skipLastSyncTimeCheck')}</List.Item>}
            </List>
          )}
        </Stack>
      ),
      labels: { confirm: t('syncMaps'), cancel: t('cancel', { ns: 'common' }) },
      confirmProps: { color: 'orange' },
      onConfirm: () =>
        void syncVehicles(selectedVehicles, {
          skip_version_check: skipVersionCheck,
          skip_last_sync_time_check: skipLastSyncTimeCheck,
        }),
    })

  return (
    <PageShell
      title={t('vehicleVersions')}
      fullWidth
      action={
        <Button size="xs" variant="light" onClick={toggleAll} disabled={syncableVehicles.length === 0}>
          {allSelected ? t('clearSelection') : t('selectAll')}
        </Button>
      }
    >
      <Stack>
        <Card>
          <Stack gap="sm">
            <Text fw={600}>{t('backendDataVersion')}</Text>
            <Table withTableBorder withColumnBorders>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>{t('schema')}</Table.Th>
                  <Table.Th>{t('dataVersionType')}</Table.Th>
                  <Table.Th>{t('mapVersion')}</Table.Th>
                  <Table.Th>{t('modifiedAt')}</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {CONTROL_DATA_SCHEMAS.map(schema => {
                  const version = backendVersions[schema]

                  return (
                    <Table.Tr key={schema}>
                      <Table.Td>
                        <Text size="sm" ff="monospace">
                          {schema}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm">{version?.type ?? '—'}</Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm" ff="monospace">
                          {version?.map_version ?? '—'}
                        </Text>
                      </Table.Td>
                      <Table.Td>
                        <Text size="sm">{version ? formatDateTime(version.modified_at) : '—'}</Text>
                      </Table.Td>
                    </Table.Tr>
                  )
                })}
              </Table.Tbody>
            </Table>
          </Stack>
        </Card>

        <Card padding={0} style={{ position: 'relative' }}>
          <LoadingOverlay visible={isLoading} />
          <Table striped highlightOnHover withColumnBorders>
            <Table.Thead>
              <Table.Tr>
                <Table.Th w={48}>
                  <Checkbox
                    aria-label={t('selectAll')}
                    checked={allSelected}
                    indeterminate={someSelected}
                    disabled={syncableVehicles.length === 0 || isSyncing}
                    onChange={toggleAll}
                  />
                </Table.Th>
                <Table.Th>{t('id')}</Table.Th>
                <Table.Th>{t('dataVersion')}</Table.Th>
                <Table.Th>{t('softwareVersion')}</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {vehicles?.map(vehicle => {
                const dataVersion = vehicle.control_data_version
                const softwareVersion = vehicle.vehicle_software_version
                const match = matchDataVersion(dataVersion, backendVersions)
                const softwareVersionValid = isSoftwareVersionValid(softwareVersion)
                const softwareVersionColor = softwareVersionValid ? undefined : HIGHLIGHTED_TEXT_COLOR

                return (
                  <Table.Tr key={vehicle.id}>
                    <Table.Td>
                      <Checkbox
                        aria-label={vehicle.id}
                        checked={!!selected[vehicle.id] && !!vehicle.vpn_address}
                        disabled={!vehicle.vpn_address || isSyncing}
                        onChange={event => toggleVehicle(vehicle.id, event.currentTarget.checked)}
                      />
                    </Table.Td>
                    <Table.Td>
                      <Text size="sm" fw={600}>
                        {vehicle.id}
                      </Text>
                    </Table.Td>
                    <Table.Td bg={DATA_VERSION_MATCH_COLOR[match]} c={HIGHLIGHTED_TEXT_COLOR}>
                      {dataVersion ? (
                        <Stack gap={2}>
                          <Text size="sm" ff="monospace" c={HIGHLIGHTED_TEXT_COLOR}>
                            {dataVersion.map_version}
                          </Text>
                          <Text size="xs" c={HIGHLIGHTED_TEXT_COLOR}>
                            {dataVersion.type}
                          </Text>
                          <Text size="xs" c={HIGHLIGHTED_TEXT_COLOR}>
                            {formatDateTime(dataVersion.modified_at)}
                          </Text>
                        </Stack>
                      ) : (
                        <Text size="sm" c={HIGHLIGHTED_TEXT_COLOR}>
                          {t('noDataVersion')}
                        </Text>
                      )}
                    </Table.Td>
                    <Table.Td
                      bg={softwareVersionValid ? undefined : INVALID_SOFTWARE_VERSION_COLOR}
                      c={softwareVersionValid ? undefined : HIGHLIGHTED_TEXT_COLOR}
                    >
                      {softwareVersion ? (
                        <Stack gap={2}>
                          <Text size="sm" ff="monospace" c={softwareVersionColor}>
                            {t('tag')}: {softwareVersionField(softwareVersion.tag) ?? t('missingTag')}
                          </Text>
                          <Text size="xs" ff="monospace" c={softwareVersionColor}>
                            {t('branch')}: {softwareVersionField(softwareVersion.branch) ?? t('missingTag')}
                          </Text>
                          <Text size="xs" ff="monospace" c={softwareVersionColor}>
                            {t('commit')}: {softwareVersionField(softwareVersion.commit) ?? t('missingTag')}
                          </Text>
                        </Stack>
                      ) : (
                        <Text size="sm" c={softwareVersionColor}>
                          {t('noSoftwareVersion')}
                        </Text>
                      )}
                    </Table.Td>
                  </Table.Tr>
                )
              })}
            </Table.Tbody>
          </Table>
        </Card>

        <Card>
          <Stack gap="sm">
            <Text fw={600}>{t('syncMaps')}</Text>
            <Text size="sm" c="dimmed">
              {t('syncMapsHelp')}
            </Text>
            <Checkbox
              label={t('skipVersionCheck')}
              description={t('skipVersionCheckHelp')}
              checked={skipVersionCheck}
              disabled={isSyncing}
              onChange={event => setSkipVersionCheck(event.currentTarget.checked)}
            />
            <Checkbox
              label={t('skipLastSyncTimeCheck')}
              description={t('skipLastSyncTimeCheckHelp')}
              checked={skipLastSyncTimeCheck}
              disabled={isSyncing}
              onChange={event => setSkipLastSyncTimeCheck(event.currentTarget.checked)}
            />
            <Group>
              <Button
                color="orange"
                leftSection={<TbRefresh size={16} />}
                loading={isSyncing}
                disabled={selectedVehicles.length === 0}
                onClick={confirmSync}
              >
                {t('syncMaps')}
              </Button>
              <Text size="sm" c="dimmed">
                {t('selectedVehiclesCount', { count: selectedVehicles.length })}
              </Text>
            </Group>
          </Stack>
        </Card>
      </Stack>
    </PageShell>
  )
}

export default VehicleVersionsPage
